import React from 'react';
import * as Sentry from '@sentry/react';

import FLAG from 'config/flag.singleton';
import RELEASE from 'config/release.singleton';
import { IGNORE } from 'constants/misc.const';
import SENTRY_LEVEL from 'constants/sentryLevel.const';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import fromOptionsToSentryData from 'utils/converters/options/fromOptionsToSentryData.util';
import { nowAsTimestamp } from 'utils/date/now.util';
import { isEnvDev, isEnvTest } from 'utils/env/determine.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import transformObject from 'utils/objects/transformObject.util';

const CTAG = 'SENTRY';
const L = logger(CTAG);

const USE_PING = isEnvDev();
const USE_NEW_REPLAY = FLAG.sentryReplay;
const USE_NEW_FEEDBACK = false;
const USE_NEW_BEFORE_SEND = false;

const BOUNCE = 5 * 60 * 1000;

const T = Object.freeze({
  //
  //
  RNEXT: 'rls.next.sha',
  RCURR: 'rls.curr.sha',
  //
  //
  CTAG: 'CTAG',
  FTAG: 'FTAG',
  //
  //
  CIN: 'ci.number',
  CIS: 'ci.sha',
  CIT: 'ci.tag',
  CIB: 'ci.branch',
  CID: 'ci.now',
  //
  //
  EE: 'environment',
  EC: 'env.conf',
  EN: 'env.node',
  ER: 'env.react',
  //
  //
});

// NOTE: keeps track of some module level state
let isInitializing = void 1;
let prevSalonId = Symbol('something unique');

// noinspection JSUnusedGlobalSymbols
const determineReplaysSessionSampleRate = (conf) => {
  if (!USE_NEW_REPLAY) return IGNORE;

  const env = conf?.ENV;

  if (isEnvTest({ env })) return 0;
  if (isEnvDev(conf)) return 0; // replay is not enabled

  return 0; // 0% in production
};

// If the entire session is not sampled, use the below sample rate to sample sessions when an error occurs.
const determineReplaysOnErrorSampleRate = () => (USE_NEW_REPLAY ? 1.0 : IGNORE);

const determineIntegrations = () =>
  [
    USE_NEW_REPLAY
      ? Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        })
      : IGNORE,

    USE_NEW_FEEDBACK
      ? Sentry.feedbackIntegration({
          // SEE: https://docs.sentry.io/platforms/javascript/guides/react/user-feedback/
          colorScheme: 'light',
        })
      : IGNORE,
  ].filter(($) => $ !== IGNORE);

// noinspection JSUnusedLocalSymbols
const determineBeforeSend = (conf) => (event, hint) => {
  // if we simply return the same event it is like not using the before send
  if (!USE_NEW_BEFORE_SEND) return event;

  if (event?.exception && event?.event_id) {
    Sentry.showReportDialog({ eventId: event.event_id });
  }

  return event;
};

const determineInitialScope = (conf) => ({
  tags: {
    [T.EE]: tag(conf?.environment),
    [T.EC]: tag(conf?.v),
    [T.ER]: tag(conf?.reactEnv),
    [T.EN]: tag(conf?.nodeEnv),
    [T.CIB]: tag(conf?.buildCiBranch),
    [T.CIN]: tag(conf?.buildCiNum),
    [T.CIT]: tag(conf?.buildCiTag),
    [T.CID]: tag(conf?.buildNow),
    [T.CIS]: tag(conf?.buildShaLong),
  },
});

const removeIgnoredEntries = (entries) => entries.filter(([, val]) => val !== IGNORE);

const determineOptions = (conf) =>
  transformObject(removeIgnoredEntries, {
    // SEE: https://develop.sentry.dev/sdk/envelopes/#size-limits
    //
    environment: tag(conf?.environment),
    release: tag(RELEASE.currSha()),
    dsn: conf?.sentryDsn,
    //
    // SEE: https://docs.sentry.io/platforms/javascript/guides/react/session-replay/configuration/
    replaysSessionSampleRate: determineReplaysSessionSampleRate(conf),
    replaysOnErrorSampleRate: determineReplaysOnErrorSampleRate(conf),
    integrations: determineIntegrations(conf),
    beforeSend: determineBeforeSend(conf),
    normalizeDepth: 5,
    initialScope: determineInitialScope(conf),
  });

const tag = ($) => fromAnyToString($) || '(empty)';

const updateReleaseTags = () => {
  RELEASE.nextSha()
    .then((n) => void Sentry.getCurrentScope().setTags({ [T.RNEXT]: tag(n) }))
    .catch((e) => void L.warn('updateReleaseTags()', e));

  Sentry.getCurrentScope().setTags({ [T.RCURR]: tag(RELEASE.currSha()) });
};

const init = (conf) => {
  const FTAG = 'init()';
  try {
    const sentryEnable = conf?.sentryEnable;
    if (!sentryEnable) {
      L.debug(FTAG, 'Sentry not enabled because of sentryEnable:', sentryEnable);
      isInitializing = false;
      return;
    }

    const isInitialized = Sentry.isInitialized();
    if (isInitializing || isInitialized) L.warn(FTAG, 'Unexpected re-init with', { isInitialized, isInitializing });
    isInitializing = true;

    if (!conf?.environment) L.info('()', 'No environment:', conf?.environment, '!');
    if (!conf?.sentryDsn) L.warn('()', 'No sentryDsn:', conf?.sentryDsn, '!');

    const options = determineOptions(conf);
    L.info(FTAG, 'Initializing with options:', options, '...');

    Sentry.init(options);

    RELEASE.subscribe(updateReleaseTags);
  } catch (e) {
    L.warn(FTAG, e);
  } finally {
    isInitializing = false;
  }
};

const salon = (options) => {
  try {
    const salonId = options?.salonId;
    const tag = fromAnyToTrimmed(options?.tag);

    if (prevSalonId !== salonId) {
      const isInitialized = Sentry.isInitialized();
      L.info('salon()', tag, 'Setting salonId:', salonId, 'w/:', { isInitialized, prevSalonId });
      prevSalonId = salonId;
    }

    Sentry.getCurrentScope().setTags({ salonId });
  } catch (e) {
    L.warn('salon()', tag, e);
  }
};

const captureMessage = (options) => {
  try {
    if (!Sentry.isInitialized()) {
      L.warn('captureMessage()', 'Not initialized. Options:', options);
      return false;
    }

    const d = fromOptionsToSentryData(options);
    const { tags, message, extra, level } = d ?? {};

    L.debug('captureMessage()', 'capturing', d, message, '...');
    return Sentry.captureMessage(message, { level, extra, tags });
  } catch (e) {
    L.warn('captureMessage()', options, e);
    return false;
  }
};

const captureAnyError = (options) => {
  const FTAG = 'captureAnyError()';

  try {
    if (!Sentry.isInitialized()) {
      L.warn(FTAG, 'Not initialized. Options:', options);
      return false;
    }

    const d = fromOptionsToSentryData(options);

    if (d.error instanceof Error) {
      L.debug(FTAG, options, 'Capturing', d, d.error, '...');
      return Sentry.captureException(d.error);
    } else {
      L.warn(FTAG, options, 'Not a proper type error:', d.error, 'from:', d);
      const message = fromAnyToString(d.error);
      return Sentry.captureMessage(message, { level: d.level, extra: d.extra, tags: d.tags });
    }
  } catch (e) {
    L.warn(FTAG, options, e);
    return false;
  }
};

const captureFetchError = (options) => {
  if (!Sentry.isInitialized()) {
    L.warn('captureFetchError()', 'Not initialized. Options:', options);
    return true;
  }

  const { base, method, path, salonId, status } = options ?? {};
  const { extra, text, json } = options ?? {};

  const logger = fromAnyToTrimmed(options?.logger) || 'captureFetchError';
  const tags = { salonId, method, base, path };

  try {
    // SEE: https://getslick.atlassian.net/browse/GS-12048
    if (status === 400 || status === 401 || status === 403 || status === 404) return;

    // NOTE: this is checking if code isn't in the form 4xx or 5xx because we're only interested in errors
    if (status < 400 || status >= 600) return;

    // NOTE: we're aware of the BE error code, so log it as just a regular info
    return captureMessage({
      logger,
      tags,
      extra: { ...asObject(extra), status, text, json },
      message: `${status} ${method} ${base}${path}`,
    });
  } catch (e) {
    // NOTE: this is an actual error of the code itself, not just a status from BE
    return captureAnyError({ logger, tags, extra: { status, text, json, extra }, error: e });
  }
};

const captureFeedback = (options) => {
  try {
    Sentry.captureUserFeedback({
      //
      // NOTE: one can also find eventId by calling Sentry.lastEventId()
      // NOTE: you can either just put eventId or regular message/error options and it will get generated
      event_id: fromAnyToTrimmed(options?.eventId) || captureAnyError(options),
      name: fromAnyToTrimmed(options?.name) || '(empty name)',
      email: fromAnyToTrimmed(options?.email) || '(empty email)',
      comments: fromAnyToTrimmed(options?.comments) || '(empty comments)',
    });
  } catch (e) {
    L.warn('captureFeedback()', e);
  }
};

const debug = (options) => captureMessage({ ...(options ?? void 1), level: SENTRY_LEVEL.debug });
const info = (options) => captureMessage({ ...(options ?? void 1), level: SENTRY_LEVEL.info });
const warn = (options) => captureMessage({ ...(options ?? void 1), level: SENTRY_LEVEL.warning });

// noinspection UnnecessaryLocalVariableJS
const ping = (options) => {
  const FTAG = 'ping()';

  if (!USE_PING) {
    L.debug(FTAG, 'Disabled by USE_PING:', USE_PING, 'aborting...');
    return false;
  }

  try {
    return debug({
      level: SENTRY_LEVEL.info,
      extra: {
        BOUNCE,
        logger: [CTAG, FTAG],
        now: nowAsTimestamp(),
        options,
      },
      tags: { CTAG, FTAG },
      message: `ping`,
    });
  } catch (e) {
    L.warn(FTAG, e);
    return false;
  }
};

// SEE: https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/
const ErrorBoundary = ({ children }) => <Sentry.ErrorBoundary showDialog>{children}</Sentry.ErrorBoundary>;

// SEE: https://docs.sentry.io/platforms/javascript/guides/react/features/redux/
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer: (action) => {
    if (action?.type === 'return null if  you do not want the action to be logged') {
      return null;
    }
    return action;
  },
  stateTransformer: (state) => ({
    ...state,
    cognito: {
      attributes: state?.cognito?.attributes,
      state: state?.cognito?.state,
      error: state?.cognito?.error,
    },
  }),
});

const SENTRY = Object.freeze({
  // tags keys constants
  T, //
  // logging utilities
  feed: captureFeedback,
  message: captureMessage,
  info,
  warn,
  error: captureAnyError,
  becall: captureFetchError,
  //
  // context utilities
  salon,
  //
  // setup utilities
  init,
  ping,
  isInitialized: Sentry.isInitialized.bind(Sentry),
  //
  // components
  ErrorBoundary,
  //
  // redux
  reduxEnhancer: sentryReduxEnhancer,
});

export default SENTRY;
